// Import Clerk hooks for authentication and user management
import { useClerk, useUser } from "@clerk/clerk-react";
// Import sidebar context for managing sidebar state
import { SidebarContext } from "Context/SidebarContext";
// Import Trending component for community page
import Trending from "components/Community/Trending";
// Import Timeline component for frond details page
import Timeline from "components/FrondDetails/Timeline";
// Import Interests component for profile page
import Interests from "components/Profile/Interests";
// Import ProfileNavbar component for the navigation bar
import ProfileNavbar from "components/ProfileNavbar/ProfileNavbar";
// Import Sidebar component for left navigation
import Sidebar from "components/Sidebar/Sidebar";
// Import RightSidebar component for right sidebar
import RightSidebar from "components/Sidebar/RightSidebar";
// Import custom hook for detecting screen size
import useSize from "hooks/useSize";
// Import React hooks and utilities
import React, { useContext, useEffect, useState } from "react";
// Import routing hooks for navigation and location
import { Outlet, useLocation } from "react-router-dom";

// Main component that wraps authenticated routes
const AuthLayout = () => {
  // Get sidebar open/closed state from context
  const { isOpen } = useContext(SidebarContext);
  // Get current window width from custom hook
  const { width } = useSize();
  // Get current URL path from react-router
  const { pathname } = useLocation();

  // State variables to control visibility of various components
  const [showTimeline, setShowTimeline] = useState(false);
  const [showTrending, setShowTrending] = useState(false);
  const [showInterests, setShowInterests] = useState(false);
  const [showRightSidebar, setShowRightSidebar] = useState(false);

  // Helper function to extract the first part of the path
  function formatPathName(link) {
    const format = link.split("/")[1];
    return format;
  }

  // Effect to show/hide Timeline based on current route
  useEffect(() => {
    const formattedPath = formatPathName(pathname);
    setShowTimeline(formattedPath === "frond");
  }, [pathname]);

  // Effect to show/hide Trending based on current route
  useEffect(() => {
    const formattedPath = formatPathName(pathname);
    setShowTrending(formattedPath === "community");
  }, [pathname]);

  // Effect to show/hide Interests based on current route
  useEffect(() => {
    const formattedPath = formatPathName(pathname);
    setShowInterests(formattedPath === "profile");
  }, [pathname]);

  // Effect to show/hide right sidebar only on the Chat with Frond screen.
  useEffect(() => {
    const formattedPath = formatPathName(pathname);
    setShowRightSidebar(
      formattedPath === "" ||
        formattedPath === "home" ||
        formattedPath === "my-fronds"
    );
  }, [pathname]);

  // Get Clerk authentication hooks
  const { session } = useClerk();
  const clerkUser = useUser();
  // Check if user is signed in via localStorage token
  const isSignedIn = localStorage.getItem("token");

  // State for user information
  const [userEmail, setUserEmail] = useState(null);
  const [userFullName, setUserFullName] = useState(null);
  const [userImg, setUserImg] = useState(null);

  // Effect to fetch user data when Clerk user changes
  useEffect(() => {
    if (session?.user && isSignedIn && clerkUser?.user) {
      setUserImg(clerkUser.user.imageUrl);
      setUserFullName(clerkUser.user.fullName);
      setUserEmail(clerkUser.user.primaryEmailAddress?.emailAddress);
    }
  }, [clerkUser.user, session, isSignedIn]);

  const shouldRenderRightSidebar = showRightSidebar && width >= 1280;
  const shouldRenderTrending = showTrending && width >= 1024;

  // Calculate widths dynamically
  const getPageWidth = () => {
    const sidebarWidth = width >= 768 ? (isOpen ? 238 : 80) : 0;
    const rightSidebarWidth = shouldRenderRightSidebar ? 360 : 0;
    const trendingWidth = shouldRenderTrending ? 337 : 0;
    
    // Calculate total width
    const totalOffset = sidebarWidth + rightSidebarWidth + trendingWidth;
    
    return `calc(100% - ${totalOffset}px)`;
  };

  const getPageMarginLeft = () => {
    if (width < 768) return 0;
    return isOpen ? 238 : 80;
  };

  return (
    <>
      {/* Render ProfileNavbar only if user exists */}
      {clerkUser?.user && (
        <ProfileNavbar
          userEmail={userEmail}
          userFullName={userFullName}
          userImg={userImg}
        />
      )}

      {/* Main wrapper container */}
      <div className="wrapper flex items-start phone:m-5 m-2.5 gap-2 relative min-h-[calc(100vh-109px)]">
        {/* Decorative background elements */}
        <div className="fixed left-0 bottom-0 w-[273.26px] h-[305.17px] z-10 bg-authLayoutBuble"></div>
        <div className="fixed right-0 top-[139px] w-[187px] h-[250px] z-10 bg-authLayoutPrint"></div>

        {/* Left sidebar */}
        <Sidebar />

        {/* Main content area with outlet for child routes */}
        <div
          className={`page relative z-20 transition-all duration-300 overflow-hidden`}
          style={{
            width: getPageWidth(),
            marginLeft: `${getPageMarginLeft()}px`,
          }}
        >
          <Outlet />
        </div>

        {/* Conditionally render component overlays */}
        {showTimeline && <Timeline />}
        {shouldRenderTrending && <Trending />}
        {showInterests && <Interests />}

        {/* Right sidebar - fixed position on the right */}
        {shouldRenderRightSidebar && (
          <div className="fixed right-0 top-[73px] z-20 hidden h-[calc(100vh-73px)] w-[360px] max-w-[28vw] overflow-y-auto border-l border-gray-200 bg-surface shadow-lg xl:block">
            <RightSidebar />
          </div>
        )}
      </div>
    </>
  );
};

// Export the component as default
export default AuthLayout;
